//------------------------------------------------------------------------
// Line charts
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
import {Chart, LineElement, PointElement, LineController, CategoryScale, LinearScale} from 'chart.js';
Chart.register(LineElement, PointElement, LineController, CategoryScale, LinearScale);
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';

// Data labels plugin to add labels above data points
// https://github.com/chartjs/chartjs-plugin-datalabels
import ChartDataLabels from 'chartjs-plugin-datalabels';

export default class LineChart {
  constructor(el) {
    this.el = el;
    if (!this.el) {
      console.warn(`Unable to initialize line chart, no matching element for “${this.el}”`);
      return false;
    }

    this.canvas = this.el.querySelector('canvas');
    if (!this.canvas) {
      console.warn('Unable to initialize line chart, no <canvas> element found');
      return false;
    }

    // Track if chart has already initialized so IntersectionObserver doesn’t try to reinitialize
    this.hasInit = false;

    // Setup observer so we can init the chart once it’s in the viewport
    this.observer = new IntersectionObserver((entries) => {
      let entry = entries[0];
      if (entry.isIntersecting && !this.hasInit) {
        this.init();
      }
    },
    {
      threshold: [0.5]
    });
    this.observer.observe(this.el);

    // Init chart in print view
    window.addEventListener('beforeprint', () => {
      this.init();
    });
  }

  init() {
    new Chart(this.canvas.getContext('2d'), {
      type: 'line',
      data: {
        labels: JSON.parse(this.canvas.getAttribute('data-labels')),
        datasets: this.getData(),
      },
      plugins: [ChartDataLabels],
      options: {
        animation: {
          animateRotate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        },
        borderWidth: 6,
        pointBorderWidth: 0,
        pointHoverRadius: 8,
        pointRadius: 8,
        maintainAspectRatio: false,// allows us to set the height via CSS
        responsive: true,
        plugins: {
          legend: false,
          datalabels: {
            align: 'top',
            anchor: 'end',
            color: '#101066',
            labels: {
              title: {
                font: {
                  family: 'Manrope, -apple-system, Segoe UI, sans-serif',
                  size: this.calcFontSize(),
                  weight: 'bold'
                }
              },
            },
            formatter: function(value, context) {
              return value + "%";
            }
          },
          tooltip: {
            enabled: false,
            // displayColors: false,
            // callbacks: {
            //   label: function(context) {
            //     return context.dataset.label + ': ' + context.formattedValue + '%';
            //   }
            // }
          }
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#101066',
              font: {
                family: 'Manrope, -apple-system, Segoe UI, sans-serif',
                size: 20,
                weight: 'bold'
              }
            }
          },
          y: {
            grid: {
              borderWidth: 0,
              display: false,
            },
            ticks: {
              display: false,
            }
          }
        }
      }
    });

    this.hasInit = true;
  }

  // Get chart data from markup and format for Chart.js
  getData() {
    // Data labels
    let labelEls = this.el.querySelectorAll('[data-label]');
    let dataLabels = [...labelEls].map(el => el.getAttribute('data-label'));

    // Data values
    let valueEls = this.el.querySelectorAll('[data-values]');
    let dataValues = [...valueEls].map(el => JSON.parse(el.getAttribute('data-values')));

    // Data colors
    let dotEls = this.el.querySelectorAll('.Legend-dot');
    let colors = [...dotEls].map(el => window.getComputedStyle(el).getPropertyValue('background-color'));

    let datasets = [];

    for (let i = 0, len = dataLabels.length; i < len; i++) {
      datasets.push({
        label: dataLabels[i],
        data: dataValues[i],
        backgroundColor: colors[i],
        borderColor: colors[i].replace(')', ', 0.3)').replace('rgb', 'rgba'),// convert rgb() to rgba()
      });
    }

    return datasets;
  }

  calcFontSize() {
    // Choose data label font size based on viewport width
    // Note: We can’t use vw or calc() so we have to calcuate the fixed pixel value
    // Note: We used “fs-scale(font-size, (375px: 14px, 640px: 20px))” to generate the constants below:
    return Math.max(14, Math.min((window.innerWidth * 0.02264) + 5.51, 20)).toFixed(1);
  }
}
